import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaignAuction } from 'api/campaigns';
import { useGivebutterForm } from 'components/form/useGivebutterForm';
import { useCurrentAuction } from 'customHooks/useCurrentAuction';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Auction } from 'types/domain';

interface AuctionCommunicationsForm {
    startSms: boolean;
    startEmail: boolean;
    endSms: boolean;
    endEmail: boolean;
    donateAskEmail: boolean;
    donateAskSms: boolean;
    outstandingInvoice: string;
    outstandingInvoiceEmailOn: boolean;
    outstandingInvoiceSmsOn: boolean;
}

const useAuctionCommunications = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = Number(campaign.id);
    const accountId = Number(campaign.account_id);

    const { data: auction } = useCurrentAuction();
    const auctionId = auction.id;

    const auctionForm = toForm(auction);

    const methods = useGivebutterForm<AuctionCommunicationsForm>({
        defaultValues: auctionForm,
    });
    const { watch } = methods;
    const invoiceEmailOn = watch('outstandingInvoiceEmailOn');
    const invoiceSmsOn = watch('outstandingInvoiceSmsOn');

    const { mutate: updateAuction } = useMutation({
        mutationFn: ({ data }: { data: Partial<Auction> }) =>
            updateCampaignAuction({ campaignId, auctionId, data }),
        onSuccess: () => {
            toast.success('Communication settings updated.');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.auction(campaignId, auctionId),
            });
        },
    });

    const onSubmit = async (data: AuctionCommunicationsForm) =>
        updateAuction({ data: toRequest(data) });

    return {
        onSubmit: methods.handleSubmit(onSubmit),
        accountId,
        isOutstandingInvoiceDropdownDisabled: !invoiceEmailOn && !invoiceSmsOn,
        methods,
    };
};

const toForm = (auction: Auction | null): AuctionCommunicationsForm => ({
    startEmail: !!(auction?.notify_auction_start_email ?? true),
    startSms: !!(auction?.notify_auction_start_sms ?? true),
    endEmail: !!(auction?.notify_auction_end_email ?? true),
    endSms: !!(auction?.notify_auction_end_sms ?? true),
    outstandingInvoice: getOutStandingInvoiceFromFrequency(
        (auction?.notify_invoice_frequency_email ?? 0) ||
            (auction?.notify_invoice_frequency_sms ?? 0),
    ),
    outstandingInvoiceEmailOn: getOutstandingInvoiceToggle(auction?.notify_invoice_frequency_email),
    outstandingInvoiceSmsOn: getOutstandingInvoiceToggle(auction?.notify_invoice_frequency_sms),
    donateAskSms: auction?.notify_auction_ended_donate_sms ?? true,
    donateAskEmail: auction?.notify_auction_ended_donate_email ?? true,
});

const toRequest = (form: AuctionCommunicationsForm): Partial<Auction> => ({
    notify_auction_start_email: reminderTimeOrZeroFromToggle(form.startEmail),
    notify_auction_end_email: reminderTimeOrZeroFromToggle(form.endEmail),
    notify_auction_start_sms: reminderTimeOrZeroFromToggle(form.startSms),
    notify_auction_end_sms: reminderTimeOrZeroFromToggle(form.endSms),
    notify_auction_ended_donate_sms: form.donateAskSms,
    notify_auction_ended_donate_email: form.donateAskEmail,
    notify_invoice_frequency_email: form.outstandingInvoiceEmailOn
        ? parseInt(form.outstandingInvoice)
        : 0,
    notify_invoice_frequency_sms: form.outstandingInvoiceSmsOn
        ? parseInt(form.outstandingInvoice)
        : 0,
});

const REMINDER_TIME = 15;
const reminderTimeOrZeroFromToggle = (toggle: boolean) => (toggle ? REMINDER_TIME : 0);

const DEFAULT_FREQUENCY = 48;
const getOutStandingInvoiceFromFrequency = (frequency?: number) => {
    if (frequency && frequency > 0) {
        return frequency.toString();
    }

    return DEFAULT_FREQUENCY.toString();
};

const getOutstandingInvoiceToggle = (frequency?: number) => {
    if (frequency) {
        return frequency > 0;
    }

    return false;
};

export { useAuctionCommunications };
